import { useEffect, useState } from 'react';
import { searchUserDirectory } from '../lib/api';
import { errorMessage, normalizeProfileText } from '../lib/utils';
import type { DirectoryUser } from '../types';
import { useToast } from '../context/ToastContext';

export default function ChairmanPicker({
  value,
  onChange,
  expected,
  onExpectedChange,
  disabled = false,
}: {
  value: string;
  onChange: (value: string) => void;
  expected: string[];
  onExpectedChange: (values: string[]) => void;
  disabled?: boolean;
}) {
  const [results, setResults] = useState<DirectoryUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<DirectoryUser | null>(null);
  const [addToExpected, setAddToExpected] = useState(true);
  const { showToast } = useToast();

  useEffect(() => {
    const query = value.trim();
    if (query.length < 3 || selected) {
      setResults([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setSearching(true);
      try {
        const matches = await searchUserDirectory(query);
        if (!cancelled) setResults(matches);
      } catch (error) {
        if (!cancelled) showToast(errorMessage(error), 'error');
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 260);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [selected, showToast, value]);

  const choose = (user: DirectoryUser) => {
    const email = user.email.toLowerCase();
    setSelected(user);
    onChange(normalizeProfileText(user.full_name));
    setResults([]);
    if (addToExpected && !expected.some((item) => item.toLowerCase() === email)) {
      onExpectedChange([...expected, email]);
    }
  };

  const toggleExpected = (checked: boolean) => {
    setAddToExpected(checked);
    if (!selected) return;
    const email = selected.email.toLowerCase();
    const next = checked
      ? expected.some((item) => item.toLowerCase() === email) ? expected : [...expected, email]
      : expected.filter((item) => item.toLowerCase() !== email);
    onExpectedChange(next);
  };

  return <div className="chairman-picker">
    <div className="attendee-search-wrap">
      <input
        value={value}
        onChange={(event) => {
          if (selected) onExpectedChange(expected.filter((item) => item.toLowerCase() !== selected.email.toLowerCase()));
          setSelected(null);
          onChange(event.target.value);
        }}
        disabled={disabled}
        maxLength={160}
        placeholder="Cari atau masukkan nama…"
        autoComplete="off"
      />
      {value.trim().length >= 3 && !selected ? <div className="directory-results">
        {searching ? <div className="directory-message">Sedang mencari pegawai berdaftar…</div> : results.length ? results.map((user) => (
          <button type="button" className="directory-result" key={user.email} onMouseDown={(event) => event.preventDefault()} onClick={() => choose(user)}>
            <strong>{user.full_name}</strong><span>{user.email}</span><small>{user.department_org ?? 'Bahagian belum disenaraikan'}</small>
          </button>
        )) : <div className="directory-message">Tiada pegawai berdaftar yang sepadan. Nama ini akan dipaparkan sahaja.</div>}
      </div> : null}
    </div>
    {selected ? <label className="chairman-expected-toggle"><input type="checkbox" checked={addToExpected} onChange={(event) => toggleExpected(event.target.checked)} /><span>Tambah Pengerusi kepada ahli dijemput</span></label> : null}
    <small className="muted">Peranan Pengerusi tidak memberikan akses <em>Host</em> atau <em>Co-Host</em>.</small>
  </div>;
}
